import { useCallback, useEffect, useMemo, useState } from "react";
import { formatDateTime } from "../../../users/adminUserFormatters";
import {
  SIATA_AMVA_CALIBRATION_REFERENCE_POINTS,
  SIATA_CALIBRATION_QA_CHECKLIST,
} from "../../../../../../constants/siataSantaElenaCalibrationConstants.js";
import {
  boundsToMapboxCoordinates,
  nudgeBounds,
  validateRadarBounds,
} from "../../../../../../utils/siataSantaElenaRadarBoundsUtils.js";
import {
  buildSiataCalibrationDiagnostics,
  getSiataCalibrationState,
  resetPreviewToApprovedBounds,
  resetPreviewToGeotiffBounds,
  setSiataCalibrationState,
} from "../../../../../../utils/siataSantaElenaCalibrationStore.js";
import {
  fetchSiataGeoref,
  fetchSiataLatestFrame,
  saveSiataCalibration,
} from "../../../../../../weather/adminRadarActionsApi.js";
import { formatFrameAge } from "../../../../../../utils/radarHealthAdminUtils.js";

const NUDGE_STEPS = [0.005, 0.01, 0.02, 0.05];
const OPACITY_PRESETS = [0.3, 0.5, 0.65, 0.9];

function BoundsField({ label, value, onChange, error }) {
  return (
    <label className="weather-radar-calibration__field">
      <span>{label}</span>
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {error ? <span className="weather-radar-calibration__field-error">{error}</span> : null}
    </label>
  );
}

const WeatherSiataRadarCalibrationPanel = ({ canManage = false, onToast }) => {
  const [serverGeoref, setServerGeoref] = useState(null);
  const [latestFrame, setLatestFrame] = useState(null);
  const [calibrationState, setCalibrationState] = useState(() => getSiataCalibrationState());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [boundsError, setBoundsError] = useState("");
  const [toast, setToast] = useState("");

  const previewBounds = calibrationState.previewBounds;
  const validation = useMemo(() => validateRadarBounds(previewBounds), [previewBounds]);

  const notify = (message, tone = "success") => {
    setToast(message);
    onToast?.({ tone, message });
  };

  const loadDiagnostics = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [georefPayload, framePayload] = await Promise.all([
        fetchSiataGeoref(),
        fetchSiataLatestFrame().catch(() => null),
      ]);
      setServerGeoref(georefPayload);
      setLatestFrame(framePayload);
      setCalibrationState(getSiataCalibrationState(georefPayload));
    } catch (error) {
      setServerGeoref(null);
      setLatestFrame(null);
      setErrorMessage(error.message || "SIATA georef endpoint unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDiagnostics();
  }, [loadDiagnostics]);

  const updateState = (patch) => {
    const result = setSiataCalibrationState(patch);
    if (!result.ok) {
      setBoundsError(result.error);
      return;
    }
    setBoundsError("");
    setCalibrationState(result.state);
  };

  const applyBounds = (nextBounds) => {
    const result = validateRadarBounds(nextBounds);
    if (!result.valid) {
      setBoundsError(result.message);
      return;
    }
    setBoundsError("");
    updateState({ previewBounds: nextBounds });
  };

  const handleCopy = async (payload, successMessage) => {
    try {
      await navigator.clipboard.writeText(
        typeof payload === "string" ? payload : JSON.stringify(payload, null, 2)
      );
      notify(successMessage);
    } catch {
      window.prompt("Copy calibration output:", JSON.stringify(payload, null, 2));
    }
  };

  const handleSave = async () => {
    const confirmed = window.confirm(
      "Save calibrated SIATA bounds to server config? Previous calibration will be backed up."
    );
    if (!confirmed) {
      return;
    }

    setSaving(true);
    try {
      const result = await saveSiataCalibration({
        bounds: previewBounds,
        notes: calibrationState.notes,
        boundsConfirmed: true,
      });
      notify(result.message || "SIATA calibration saved.");
      await loadDiagnostics();
    } catch (error) {
      notify(error.message || "Unable to save calibration.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenMapPreview = () => {
    if (typeof window !== "undefined") {
      window.__MIDPOINT_SIATA_CALIBRATION_DEBUG__ = true;
      window.__MIDPOINT_SIATA_DEBUG_BOUNDS__ = true;
    }
    updateState({ active: true, showBoundsOutline: true });
    window.location.assign("/?perspective=weather&siataCalibration=1");
  };

  if (!canManage) {
    return (
      <section id="siata-radar-calibration" className="weather-radar-calibration panel-card">
        <h2 className="admin-section-title">SIATA Radar Calibration</h2>
        <p className="weather-radar-calibration__notice">
          SIATA calibration tools require weather layer manage permission.
        </p>
      </section>
    );
  }

  const previousBounds = serverGeoref?.calibration?.bounds || serverGeoref?.manualBounds || null;

  return (
    <section id="siata-radar-calibration" className="weather-radar-calibration panel-card">
      <div className="weather-radar-calibration__header">
        <div>
          <h2 className="admin-section-title">SIATA Santa Elena Radar Calibration</h2>
          <p className="weather-radar-calibration__description">
            GeoTIFF alignment QA for Aburrá Valley local radar. Preview nudges are session-only until
            explicitly saved.
          </p>
        </div>
        <button type="button" className="admin-dashboard__action-btn" onClick={loadDiagnostics}>
          Refresh
        </button>
      </div>

      {toast ? (
        <p className="weather-radar-calibration__toast" role="status">
          {toast}
        </p>
      ) : null}

      {loading ? <p className="muted">Loading SIATA georeference diagnostics…</p> : null}
      {errorMessage ? <p className="admin-re-error">{errorMessage}</p> : null}

      <div className="weather-radar-calibration__summary">
        <div>
          <span className="weather-radar-calibration__label">Active georef source</span>
          <strong>{serverGeoref?.activeGeorefSource || "—"}</strong>
        </div>
        <div>
          <span className="weather-radar-calibration__label">Confidence</span>
          <span>{serverGeoref?.confidence || calibrationState.confidence || "—"}</span>
        </div>
        <div>
          <span className="weather-radar-calibration__label">GeoTIFF available</span>
          <span>
            {serverGeoref?.geotiff?.available || serverGeoref?.geotiff?.extraction?.ok
              ? "Yes"
              : "No"}
          </span>
        </div>
        <div>
          <span className="weather-radar-calibration__label">Latest frame</span>
          <span>
            {latestFrame?.latestFrameTime
              ? formatDateTime(latestFrame.latestFrameTime)
              : "—"}
          </span>
        </div>
        <div>
          <span className="weather-radar-calibration__label">Frame age</span>
          <span>{formatFrameAge(latestFrame?.latestFrameAgeMinutes)}</span>
        </div>
      </div>

      {serverGeoref?.warnings?.length ? (
        <ul className="weather-radar-calibration__warnings">
          {serverGeoref.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}

      <div className="weather-radar-calibration__toggles">
        <label>
          <input
            type="checkbox"
            checked={calibrationState.showBoundsOutline}
            onChange={(event) => updateState({ showBoundsOutline: event.target.checked })}
          />
          Show SIATA bounds rectangle
        </label>
        <label>
          <input
            type="checkbox"
            checked={calibrationState.showReferenceMarkers}
            onChange={(event) => updateState({ showReferenceMarkers: event.target.checked })}
          />
          Show AMVA reference markers
        </label>
      </div>

      <div className="weather-radar-calibration__section">
        <h3>Opacity preview</h3>
        <label className="weather-radar-calibration__slider">
          <span>SIATA opacity ({Math.round(calibrationState.opacity * 100)}%)</span>
          <input
            type="range"
            min="0.1"
            max="1"
            step="0.05"
            value={calibrationState.opacity}
            onChange={(event) => updateState({ opacity: Number(event.target.value) })}
          />
        </label>
        <div className="weather-radar-calibration__presets">
          {OPACITY_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
              onClick={() => updateState({ opacity: preset })}
            >
              {Math.round(preset * 100)}%
            </button>
          ))}
        </div>
      </div>

      <div className="weather-radar-calibration__section">
        <h3>Bounds adjustment (preview only)</h3>
        <div className="weather-radar-calibration__bounds-grid">
          <BoundsField
            label="westLng"
            value={previewBounds.westLng}
            onChange={(value) => applyBounds({ ...previewBounds, westLng: value })}
          />
          <BoundsField
            label="southLat"
            value={previewBounds.southLat}
            onChange={(value) => applyBounds({ ...previewBounds, southLat: value })}
          />
          <BoundsField
            label="eastLng"
            value={previewBounds.eastLng}
            onChange={(value) => applyBounds({ ...previewBounds, eastLng: value })}
          />
          <BoundsField
            label="northLat"
            value={previewBounds.northLat}
            onChange={(value) => applyBounds({ ...previewBounds, northLat: value })}
          />
        </div>
        {boundsError || !validation.valid ? (
          <p className="admin-re-error">{boundsError || validation.message}</p>
        ) : null}

        <div className="weather-radar-calibration__nudge">
          <label>
            Nudge step
            <select
              value={calibrationState.nudgeStep}
              onChange={(event) => updateState({ nudgeStep: Number(event.target.value) })}
            >
              {NUDGE_STEPS.map((step) => (
                <option key={step} value={step}>
                  {step}
                </option>
              ))}
            </select>
          </label>
          <div className="weather-radar-calibration__nudge-grid">
            {["west", "east", "north", "south", "expandX", "contractX", "expandY", "contractY"].map(
              (direction) => (
                <button
                  key={direction}
                  type="button"
                  className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
                  onClick={() =>
                    applyBounds(
                      nudgeBounds(previewBounds, direction, calibrationState.nudgeStep)
                    )
                  }
                >
                  {direction}
                </button>
              )
            )}
          </div>
        </div>

        <div className="weather-radar-calibration__actions">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={() => {
              const result = resetPreviewToGeotiffBounds(calibrationState);
              if (!result.ok) {
                setBoundsError(result.error);
              } else {
                setCalibrationState(result.state);
                notify("Reset to GeoTIFF bounds.");
              }
            }}
          >
            Reset to GeoTIFF
          </button>
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={() => {
              const result = resetPreviewToApprovedBounds(calibrationState);
              setCalibrationState(result.state);
              notify("Reset to approved manual bounds.");
            }}
          >
            Reset to approved manual
          </button>
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={() =>
              handleCopy(
                {
                  bounds: previewBounds,
                  mapboxCoordinates: boundsToMapboxCoordinates(previewBounds),
                },
                "Candidate bounds copied."
              )
            }
          >
            Copy candidate bounds
          </button>
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={() =>
              handleCopy(buildSiataCalibrationDiagnostics(calibrationState), "Diagnostics copied.")
            }
          >
            Copy diagnostics
          </button>
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={handleOpenMapPreview}
          >
            Open map preview
          </button>
          <button
            type="button"
            className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
            onClick={handleSave}
            disabled={saving || !validation.valid}
          >
            {saving ? "Saving…" : "Save calibrated bounds"}
          </button>
        </div>

        {previousBounds ? (
          <p className="muted">
            Previous saved bounds: west {previousBounds.westLng}, south {previousBounds.southLat},
            east {previousBounds.eastLng}, north {previousBounds.northLat}
          </p>
        ) : null}
      </div>

      <div className="weather-radar-calibration__section">
        <h3>Reference points</h3>
        <ul className="weather-radar-calibration__reference-list">
          {SIATA_AMVA_CALIBRATION_REFERENCE_POINTS.map((point) => (
            <li key={point.id}>
              <strong>{point.name}</strong> — {point.lng}, {point.lat}
            </li>
          ))}
        </ul>
      </div>

      <div className="weather-radar-calibration__section">
        <h3>Alignment QA checklist</h3>
        <ul className="weather-radar-calibration__checklist">
          {SIATA_CALIBRATION_QA_CHECKLIST.map((item) => (
            <li key={item}>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(calibrationState.checklist?.[item])}
                  onChange={(event) =>
                    updateState({
                      checklist: {
                        ...calibrationState.checklist,
                        [item]: event.target.checked,
                      },
                    })
                  }
                />
                {item}
              </label>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default WeatherSiataRadarCalibrationPanel;
